import { hex, Rng, type FxMeta } from "pixelfx";

/**
 * Облако светящихся частиц — приём из моушен-графики (образец — стиль «Частицы» монтажёра, переписан под мир 60 Гц):
 *   gather — сотни точек стягиваются к точке и вспыхивают (перед суперприёмом);
 *   vortex — вихрь вокруг точки (попадание суперприёма);
 *   burst  — взрыв наружу со шлейфом (финальный удар).
 * Детерминированно: свой ГСЧ с сидом от кадра события. Только свет и полупрозрачные точки — бойцы видны сквозь.
 */
export const meta: FxMeta & { options: Record<string, unknown> } = {
  name: "Particle cloud",
  duration: 1.2,
  loop: false,
  fps: 60,
  size: [400, 300],
  origin: [200, 150],
  options: { seed: 1, mode: "gather", count: 260, radius: 150, color: "#ffd24a", hot: "#fff6d0" },
};

interface CloudOptions {
  seed: number;
  mode: "gather" | "vortex" | "burst";
  count: number;
  radius: number;
  color: string;
  hot: string;
}

interface Painter {
  blend(x: number, y: number, color: number, alpha: number): void;
  light(x: number, y: number, color: number, k: number): void;
}

const SPANS: Readonly<Record<CloudOptions["mode"], number>> = { gather: 0.42, vortex: 0.9, burst: 1.1 };

export function create(options: CloudOptions): { update(dt: number): void; draw(painter: unknown): void } {
  const random = new Rng(options.seed);
  const color = hex(options.color);
  const hot = hex(options.hot);
  const span = SPANS[options.mode];
  const dots = Array.from({ length: options.count }, () => ({
    angle: random.range(0, Math.PI * 2),
    distance: random.range(0.25, 1) * options.radius,
    delay: random.range(0, 0.25) * span,
    spin: random.range(2.5, 6) * (random.next() < 0.5 ? -1 : 1),
    size: random.next() < 0.18 ? 2 : 1,
  }));
  let time = 0;
  return {
    update(dt: number): void {
      time += dt;
    },
    draw(target: unknown): void {
      const painter = target as Painter;
      for (const dot of dots) {
        const local = Math.min(1, Math.max(0, (time - dot.delay) / (span - dot.delay)));
        if (local <= 0 || local >= 1) continue;
        let radius: number;
        let angle = dot.angle;
        let alpha: number;
        if (options.mode === "gather") {
          const ease = local * local * (3 - 2 * local);
          radius = dot.distance * (1 - ease);
          angle += ease * dot.spin * 0.6;
          alpha = 0.25 + 0.75 * ease;
        } else if (options.mode === "vortex") {
          radius = dot.distance * (0.35 + 0.65 * local);
          angle += local * dot.spin * 2;
          alpha = 1 - local;
        } else {
          const ease = 1 - (1 - local) * (1 - local) * (1 - local);
          radius = dot.distance * 1.6 * ease;
          angle += ease * dot.spin * 0.25;
          alpha = (1 - local) * (1 - local);
        }
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius * 0.7;
        for (let dx = 0; dx < dot.size; dx++)
          for (let dy = 0; dy < dot.size; dy++)
            painter.blend(x + dx, y + dy, local > 0.85 && options.mode === "gather" ? hot : color, alpha);
        painter.light(x, y, color, 0.35 * alpha);
      }
      if (options.mode === "gather" && time > span * 0.8 && time < span * 1.05) painter.light(0, 0, hot, 0.9);
    },
  };
}
